'use client';

import { useCallback, useEffect, useState } from 'react';
import type { PendingTikTokData } from './types';

const PENDING_TIKTOK_STORAGE_KEY = 'pendingTiktokData';

export type { PendingTikTokData };

/**
 * TikTok OAuth 중 임시로 저장되는 pending data 관리
 * sessionStorage 기반
 *
 * @example
 * const { data, setData, clear } = useTikTokPending();
 */
export function useTikTokPending() {
  const [data, setDataState] = useState<PendingTikTokData | null>(null);

  // 초기 로드
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const stored = sessionStorage.getItem(PENDING_TIKTOK_STORAGE_KEY);
    if (stored) {
      try {
        setDataState(JSON.parse(stored));
      } catch {
        sessionStorage.removeItem(PENDING_TIKTOK_STORAGE_KEY);
      }
    }
  }, []);

  const setData = useCallback((newData: PendingTikTokData | null) => {
    setDataState(newData);
    if (typeof window === 'undefined') {
      return;
    }

    if (newData) {
      sessionStorage.setItem(PENDING_TIKTOK_STORAGE_KEY, JSON.stringify(newData));
    } else {
      sessionStorage.removeItem(PENDING_TIKTOK_STORAGE_KEY);
    }
  }, []);

  const clear = useCallback(() => {
    setData(null);
  }, [setData]);

  return { data, setData, clear };
}
